<template>
  <div class="verify-zoho-page py-3">
    <header class="vz-toolbar">
      <div class="vz-toolbar__title">
        <span class="vz-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Verify Zoho Invoices</h1>
          <p>Cross-check the OMS export for a date against a downloaded Zoho CSV</p>
        </div>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="vz-alert">{{ errorMessage }}</CAlert>

    <section class="vz-section">
      <header class="vz-section__head">
        <h2>Verify</h2>
        <small class="vz-section__hint">Upload the Zoho CSV for the same export date</small>
      </header>
      <div class="vz-section__body">
        <form class="vz-filters" @submit.prevent="verifyOrders">
          <div class="vz-field vz-field--file">
            <label class="vz-label">Zoho CSV</label>
            <div class="vz-file">
              <input
                ref="csvInput"
                type="file"
                class="vz-file__input"
                accept="application/csv,.csv,text/csv"
                @change="onFileChange"
              >
              <button type="button" class="vz-file__btn" @click="triggerFile">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
                </svg>
                <span>{{ file ? 'Change file' : 'Choose file' }}</span>
              </button>
              <div v-if="file" class="vz-file__chip">
                <span class="vz-file__name" :title="file.name">{{ file.name }}</span>
                <button type="button" class="vz-file__clear" aria-label="Remove file" @click="clearFile">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <span v-else class="vz-file__hint">No file chosen</span>
            </div>
          </div>

          <div class="vz-field vz-field--date">
            <label class="vz-label">Export Date</label>
            <SingleDatePicker :value="exportDate" @input="exportDate = $event" />
          </div>

          <div class="vz-field vz-field--action">
            <label class="vz-label" aria-hidden="true">&nbsp;</label>
            <button type="submit" class="vz-verify-btn" :class="{ 'is-loading': isLoading }" :disabled="isLoading">
              <svg v-if="!isLoading" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              <span>{{ isLoading ? 'Verifying…' : 'Verify' }}</span>
            </button>
          </div>
        </form>
      </div>
    </section>

    <section v-if="showResults" class="vz-results">
      <article class="vz-result-card vz-result-card--amber">
        <header class="vz-result-card__head">
          <div class="vz-result-card__title">
            <span class="vz-result-card__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </span>
            <div>
              <h2>Tax Mismatches</h2>
              <small>Found in both, amounts disagree</small>
            </div>
          </div>
          <span class="vz-result-card__count">{{ comparableData.length }}</span>
        </header>
        <div class="vz-result-card__body">
          <p v-if="!comparableData.length" class="vz-empty">All matched rows agree on tax and total.</p>
          <ul v-else class="vz-result-list">
            <li v-for="(row, idx) in comparableData" :key="`tm-${idx}`" class="vz-row">
              <div class="vz-row__head">
                <span class="vz-row__order">{{ row.order_number || '—' }}</span>
                <span class="vz-row__invoice">{{ row.invoice_number || '—' }}</span>
              </div>
              <div class="vz-row__deltas">
                <span class="vz-delta" :class="deltaClass(row.TOTAL_C)" title="Total difference (Zoho − OMS)">Δ Total {{ formatDelta(row.TOTAL_C) }}</span>
                <span v-if="hasDelta(row.CGST_C)" class="vz-delta" :class="deltaClass(row.CGST_C)">CGST {{ formatDelta(row.CGST_C) }}</span>
                <span v-if="hasDelta(row.SGST_C)" class="vz-delta" :class="deltaClass(row.SGST_C)">SGST {{ formatDelta(row.SGST_C) }}</span>
                <span v-if="hasDelta(row.IGST_C)" class="vz-delta" :class="deltaClass(row.IGST_C)">IGST {{ formatDelta(row.IGST_C) }}</span>
              </div>
            </li>
          </ul>
        </div>
      </article>

      <article class="vz-result-card vz-result-card--rose">
        <header class="vz-result-card__head">
          <div class="vz-result-card__title">
            <span class="vz-result-card__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </span>
            <div>
              <h2>Missing in Zoho</h2>
              <small>In OMS for this date, no matching Zoho row</small>
            </div>
          </div>
          <span class="vz-result-card__count">{{ missingInZoho.length }}</span>
        </header>
        <div class="vz-result-card__body">
          <p v-if="!missingInZoho.length" class="vz-empty">Every OMS order has a Zoho counterpart.</p>
          <ul v-else class="vz-result-list">
            <li v-for="(row, idx) in missingInZoho" :key="`mz-${idx}`" class="vz-row">
              <div class="vz-row__head">
                <span class="vz-row__order">{{ row.order_number || '—' }}</span>
                <span class="vz-row__invoice">{{ row.invoice_number || '—' }}</span>
              </div>
              <div class="vz-row__deltas">
                <span class="vz-delta vz-delta--info">Grand Total {{ formatAmount(row.grand_total) }}</span>
              </div>
            </li>
          </ul>
        </div>
      </article>

      <article class="vz-result-card vz-result-card--slate">
        <header class="vz-result-card__head">
          <div class="vz-result-card__title">
            <span class="vz-result-card__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="8" y1="6" x2="21" y2="6" />
                <line x1="8" y1="12" x2="21" y2="12" />
                <line x1="8" y1="18" x2="21" y2="18" />
                <line x1="3" y1="6" x2="3.01" y2="6" />
                <line x1="3" y1="12" x2="3.01" y2="12" />
                <line x1="3" y1="18" x2="3.01" y2="18" />
              </svg>
            </span>
            <div>
              <h2>Sequence Gaps</h2>
              <small>Order numbers skipped in the OMS series</small>
            </div>
          </div>
          <span class="vz-result-card__count">{{ missingOrders.length }}</span>
        </header>
        <div class="vz-result-card__body">
          <p v-if="!missingOrders.length" class="vz-empty">No gaps in the order-number sequence.</p>
          <div v-else class="vz-chips">
            <span v-for="(num, idx) in missingOrders" :key="`mo-${idx}`" class="vz-num">MR-{{ num }}</span>
          </div>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import axios from 'axios'
import SingleDatePicker from '@/components/SingleDatePicker.vue'

export default {
  name: 'VerifyZohoInvoices',
  components: { SingleDatePicker },
  data () {
    return {
      file: null,
      exportDate: new Date().toISOString().slice(0, 10),
      isLoading: false,
      errorMessage: '',
      showResults: false,
      comparableData: [],
      missingInZoho: [],
      missingOrders: []
    }
  },
  methods: {
    authToken () {
      return localStorage.getItem('api_token')
    },
    triggerFile () {
      const input = this.$refs.csvInput
      if (input) input.click()
    },
    onFileChange (event) {
      this.file = (event.target.files && event.target.files[0]) || null
    },
    clearFile () {
      this.file = null
      if (this.$refs.csvInput) this.$refs.csvInput.value = ''
    },
    hasDelta (value) {
      const n = Number(value)
      return Number.isFinite(n) && Math.abs(n) > 0.001
    },
    deltaClass (value) {
      const n = Number(value)
      if (!Number.isFinite(n) || Math.abs(n) < 0.001) return 'vz-delta--neutral'
      return n > 0 ? 'vz-delta--up' : 'vz-delta--down'
    },
    formatDelta (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      const sign = n > 0 ? '+' : (n < 0 ? '−' : '')
      return `${sign}${Math.abs(n).toFixed(2)}`
    },
    formatAmount (value) {
      const n = Number(value)
      if (!Number.isFinite(n)) return '—'
      return n.toFixed(2)
    },
    verifyOrders () {
      this.errorMessage = ''
      this.showResults = false
      this.comparableData = []
      this.missingInZoho = []
      this.missingOrders = []

      if (!this.file) {
        this.errorMessage = 'Please choose a Zoho CSV file.'
        return
      }
      if (!this.exportDate) {
        this.errorMessage = 'Please pick the export date.'
        return
      }

      const formData = new FormData()
      formData.append('csv', this.file)
      formData.append('export_date', this.exportDate)

      this.isLoading = true
      axios.post(
        `${this.$apiAdress}/api/verifyorders?token=${this.authToken()}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
        .then((response) => {
          const data = response.data || {}
          this.comparableData = Array.isArray(data.comparable_data) ? data.comparable_data : []
          this.missingInZoho = Array.isArray(data.missing_in_zoho) ? data.missing_in_zoho : []
          this.missingOrders = Array.isArray(data.missing_orders) ? data.missing_orders : []
          this.showResults = true
        })
        .catch((error) => {
          console.log(error)
          const detail = error && error.response && error.response.data && (error.response.data.message || error.response.data.error)
          this.errorMessage = detail ? `Unable to verify invoices: ${detail}` : 'Unable to verify invoices.'
        })
        .finally(() => {
          this.isLoading = false
        })
    }
  }
}
</script>

<style scoped>
.verify-zoho-page {
  padding: 0.25rem 0;
}

.vz-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.vz-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.vz-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.vz-toolbar__title p {
  margin: 0.1rem 0 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.vz-toolbar__accent {
  width: 4px;
  height: 32px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
}

.vz-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

.vz-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.vz-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
  border-radius: 12px 12px 0 0;
}

.vz-section__head h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.vz-section__hint {
  color: #6b7280;
  font-size: 0.78rem;
}

.vz-section__body {
  padding: 1rem;
}

.vz-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.vz-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.vz-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.vz-field--file {
  flex: 1 1 320px;
  min-width: 260px;
}

.vz-field--date {
  flex: 0 1 240px;
  min-width: 200px;
}

.vz-field--action {
  flex: 0 0 auto;
  min-width: 130px;
}

.vz-file {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 36px;
  flex-wrap: wrap;
}

.vz-file__input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.vz-file__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 36px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-size: 0.82rem;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.vz-file__btn:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.vz-file__btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.vz-file__chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  max-width: 100%;
  padding: 0.25rem 0.4rem 0.25rem 0.65rem;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #166534;
}

.vz-file__name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vz-file__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 0;
  background: rgba(22, 101, 52, 0.12);
  color: #166534;
  cursor: pointer;
  transition: background 0.15s ease;
}

.vz-file__clear:hover {
  background: rgba(22, 101, 52, 0.22);
}

.vz-file__hint {
  font-size: 0.78rem;
  color: #9ca3af;
}

.vz-verify-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 1.1rem;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(135deg, #0e5b46 0%, #16a07a 100%);
  border: 0;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
  cursor: pointer;
}

.vz-verify-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
}

.vz-verify-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.28);
}

.vz-verify-btn:disabled,
.vz-verify-btn.is-loading {
  opacity: 0.85;
  cursor: progress;
}

.vz-results {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 0.85rem;
}

.vz-result-card {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.vz-result-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef1f7;
}

.vz-result-card__title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.vz-result-card__title h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.vz-result-card__title small {
  display: block;
  margin-top: 0.05rem;
  font-size: 0.72rem;
  color: #6b7280;
}

.vz-result-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.vz-result-card--amber .vz-result-card__head { background: #fffbeb; }
.vz-result-card--amber .vz-result-card__icon { background: #fef3c7; color: #92400e; }
.vz-result-card--amber .vz-result-card__count { background: #fef3c7; color: #92400e; }

.vz-result-card--rose .vz-result-card__head { background: #fff1f2; }
.vz-result-card--rose .vz-result-card__icon { background: #ffe4e6; color: #9f1239; }
.vz-result-card--rose .vz-result-card__count { background: #ffe4e6; color: #9f1239; }

.vz-result-card--slate .vz-result-card__head { background: #f1f5f9; }
.vz-result-card--slate .vz-result-card__icon { background: #e2e8f0; color: #334155; }
.vz-result-card--slate .vz-result-card__count { background: #e2e8f0; color: #334155; }

.vz-result-card__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 24px;
  padding: 0 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 700;
}

.vz-result-card__body {
  flex: 1 1 auto;
  padding: 0.75rem 1rem 1rem;
  max-height: 420px;
  overflow-y: auto;
}

.vz-empty {
  margin: 0;
  padding: 0.85rem;
  font-size: 0.82rem;
  color: #6b7280;
  text-align: center;
  background: #fbfcfe;
  border: 1px dashed #e5e9f2;
  border-radius: 10px;
}

.vz-result-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.vz-row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid #eef1f7;
  border-radius: 10px;
  background: #fbfcfe;
}

.vz-row__head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.vz-row__order {
  font-weight: 700;
  color: #1f2a44;
}

.vz-row__invoice {
  font-size: 0.75rem;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

.vz-row__deltas {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
}

.vz-delta {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.vz-delta--up { background: #fef3c7; color: #92400e; }
.vz-delta--down { background: #e0edff; color: #1e40af; }
.vz-delta--neutral { background: #eef1f7; color: #4b5563; }
.vz-delta--info { background: #dff5ea; color: #0e5b46; }

.vz-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.vz-num {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
  font-variant-numeric: tabular-nums;
}
</style>
